import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useCategoryApi from "hooks/api/private/useCategoryApi";
import useSnackbar from "hooks/useSnackbar";

import { CategoryListItem } from "types/api/category";

export type CategoryListHook = {
    navigateToNewCategory: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<CategoryListItem>;
    alertConfirmHook: AlertConfirmHookType;
};

function useCategoryList(): CategoryListHook {
    const { getAllCategory, deleteCategory } = useCategoryApi();

    const navigate = useNavigate();

    const navigateToEditCategory = React.useCallback(
        (row: CategoryListItem) => {
            navigate(makeUrl(":categoryId/modifica", { categoryId: row.id }));
        },
        [navigate],
    );

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "categories_language.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    type: {
                        key: "categories.type",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "category-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;
                newForm.search.type = value;
                newForm.search.slug = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Categoria",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteCategoryButton = React.useCallback(
        (row: CategoryListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la categoria?", () => {
                const [apiCall] = deleteCategory({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Categoria eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione della categoria"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteCategory,
        ],
    );

    const columns: Array<Column<CategoryListItem>> = React.useMemo(
        () => [
            {
                id: "categories.graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: CategoryListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "categories_language.name",
                header: "Nome",
                render: (row: CategoryListItem) => row.name,
                orderable: true,
            },
            {
                id: "categories.type",
                header: "Tipologia",
                render: (row: CategoryListItem) => row.type,
                orderable: true,
            },
            {
                id: "categories_language.slug",
                header: "Slug",
                render: (row: CategoryListItem) => row.slug,
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: CategoryListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditCategory}
                        onDelete={deleteCategoryButton}
                    />
                ),
            },
        ],
        [navigateToEditCategory, deleteCategoryButton],
    );

    const dataTableHook = useDataTableWithMemory<CategoryListItem>({
        apiCall: getAllCategory,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "category-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewCategory = React.useCallback(() => {
        navigate(makeUrl("nuova"));
    }, [navigate]);

    return React.useMemo(
        (): CategoryListHook => ({
            navigateToNewCategory,
            onSearchFilterChange,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        }),
        [
            navigateToNewCategory,
            onSearchFilterChange,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        ],
    );
}

export default useCategoryList;
